import { Component, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ApiV1Service } from '../core/api/apiv1.service';
import {
  NotificationService,
  NotificationType,
} from '../notification/notification/notification.service';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmComponent } from './confirm/confirm.component';
import { ApiService } from '../core/api/api.service';

interface Collaborator {
  _id: string;
  provider: string;
  name: string;
  email: string;
  company_id: string;
  collaborator: number;
  role: string;
}

export interface SubscriptionResponse {
  companyName: string;
  subscription: {
    startDate: string;
    endDate: string;
    gamesAvailable: number;
    usage: {
      gamesUsed: number;
      scormsUsed: number;
    };
    status: string;
    _id: string;
  };
}
@Component({
  selector: 'app-settings',
  templateUrl: './settings.component.html',
  styleUrl: './settings.component.sass',
})
export class SettingsComponent implements OnInit {
  queryForm: FormGroup;
  passwordForm: FormGroup;
  collaboratorForm: FormGroup;
  resetPasswordForm: FormGroup;
  showCollaboratorForm = false;
  collaborators: Collaborator[] = [];
  selectedCollaboratorId: Collaborator | null = null;
  editCollaborator: Collaborator | null = null;
  loading: 'delete' | 'add' | 'reset' | null = null;
  showMenu: boolean;
  company: Company | null = null;
  localCompanyData = localStorage['auth']
    ? JSON.parse(localStorage?.['auth'])
    : null;
  buttons = [
    { label: 'COLLABORATORS', view: 'collaborators' },
    { label: 'HELP CENTER', view: 'help' },
    { label: 'CHANGE PASSWORD', view: 'password' },
    { label: 'SUBSCRIPTION', view: 'subscription' },
  ];
  subscriptionSettings: SubscriptionResponse | null = null;
  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private fb: FormBuilder,
    private _api: ApiV1Service,
    private _apiV2: ApiService,
    private _notificationService: NotificationService,
    private _dialogModal: MatDialog,
  ) {
    this.queryForm = this.fb.group({
      message: ['', Validators.required], // Form control for the message
    });
    this.passwordForm = this.fb.group({
      currentPassword: ['', Validators.required],
      newPassword: [
        '',
        [
          Validators.required,
          Validators.minLength(8),
          // At least one uppercase, one lowercase, one digit, and one special character
          Validators.pattern(
            /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^\w\s]).{8,}$/,
          ),
        ],
      ],
      confirmPassword: ['', Validators.required],
    });

    this.collaboratorForm = this.fb.group({
      name: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      // Role field removed here as requested
    });

    this.resetPasswordForm = this.fb.group({
      newPassword: [
        '',
        [
          Validators.required,
          Validators.minLength(8),
          Validators.pattern(
            /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^\w\s]).{8,}$/,
          ),
        ],
      ],
      confirmPassword: ['', Validators.required],
    });

    // continued inside constructor()
    this.resetPasswordForm.addValidators(
      this.createCompareValidator(
        //@ts-ignore
        this.resetPasswordForm.get('newPassword'),
        this.resetPasswordForm.get('confirmPassword'),
      ),
    );

    this.passwordForm.addValidators(
      this.createCompareValidator(
        //@ts-ignore
        this.passwordForm.get('newPassword'),
        this.passwordForm.get('confirmPassword'),
      ),
    );
    console.log(this.passwordForm);
  }
  currentView = '';
  isChagingPassword = false;

  // Password helpers for guidance and strength meter
  get newPasswordValue(): string {
    return this.passwordForm?.get('newPassword')?.value || '';
  }

  get passwordLengthOk(): boolean {
    return this.newPasswordValue?.length >= 8;
  }

  get passwordHasUpper(): boolean {
    return /[A-Z]/.test(this.newPasswordValue);
  }

  get passwordHasLower(): boolean {
    return /[a-z]/.test(this.newPasswordValue);
  }

  get passwordHasDigit(): boolean {
    return /\d/.test(this.newPasswordValue);
  }

  get passwordHasSpecial(): boolean {
    return /[^\w\s]/.test(this.newPasswordValue);
  }

  get passwordStrengthScore(): number {
    let score = 0;
    if (this.passwordLengthOk) score++;
    if (this.passwordHasUpper) score++;
    if (this.passwordHasLower) score++;
    if (this.passwordHasDigit) score++;
    if (this.passwordHasSpecial) score++;
    return score; // 0 - 5
  }

  get passwordStrengthLabel(): string {
    const score = this.passwordStrengthScore;
    if (score <= 2) return 'Weak';
    if (score === 3) return 'Fair';
    if (score === 4) return 'Strong';
    return 'Very strong';
  }

  // Reset Password helpers (for collaborator reset form)
  get resetNewPasswordValue(): string {
    return this.resetPasswordForm?.get('newPassword')?.value || '';
  }

  get resetPasswordLengthOk(): boolean {
    return this.resetNewPasswordValue?.length >= 8;
  }

  get resetPasswordHasUpper(): boolean {
    return /[A-Z]/.test(this.resetNewPasswordValue);
  }

  get resetPasswordHasLower(): boolean {
    return /[a-z]/.test(this.resetNewPasswordValue);
  }

  get resetPasswordHasDigit(): boolean {
    return /\d/.test(this.resetNewPasswordValue);
  }

  get resetPasswordHasSpecial(): boolean {
    return /[^\w\s]/.test(this.resetNewPasswordValue);
  }

  get resetPasswordStrengthScore(): number {
    let score = 0;
    if (this.resetPasswordLengthOk) score++;
    if (this.resetPasswordHasUpper) score++;
    if (this.resetPasswordHasLower) score++;
    if (this.resetPasswordHasDigit) score++;
    if (this.resetPasswordHasSpecial) score++;
    return score;
  }

  get resetPasswordStrengthLabel(): string {
    const score = this.resetPasswordStrengthScore;
    if (score <= 2) return 'Weak';
    if (score === 3) return 'Fair';
    if (score === 4) return 'Strong';
    return 'Very strong';
  }

  ngOnInit(): void {
    this.route.queryParams.subscribe((params) => {
      this.currentView = params['view'] || ''; // Set to empty string if no 'view' query param
    });
    this.loadSubscriptionData();
    this.loadCompanyData();
    this.loadCollaborators();
  }

  loadCompanyData() {
    this._api
      .get(`/api/company/${this.localCompanyData?.companyUser?.company?._id}`)
      .subscribe((data: any) => {
        this.company = data;
      });
  }

  loadSubscriptionData() {
    this._apiV2
      .get<SubscriptionResponse>(
        `/genie/journey/subscription-details/${this.localCompanyData?.companyUser?.company?._id}`,
      )
      .subscribe((data) => {
        this.subscriptionSettings = data;
      });
  }

  createCompareValidator(
    controlOne: AbstractControl,
    controlTwo: AbstractControl,
  ) {
    return () => {
      if (controlOne.value !== controlTwo.value) {
        return { match_error: 'Passwords do not match' };
      }

      return null;
    };
  }

  updateView(view: string) {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { view },
      queryParamsHandling: 'merge',
    });
    this.showMenu = false;
    this.selectedCollaboratorId = null;
    this.showCollaboratorForm = false;
  }

  submitQuery() {
    if (!this.queryForm.valid) {
      this.queryForm.markAllAsTouched();
      return;
    }
    const formValue = this.queryForm.value;

    // Send form value to the API
    this._api
      .post('/api/company/query', {
        company_id: `${this.localCompanyData?.companyUser?.company?._id}`,
        subject: `Dashboard query from ${this.localCompanyData?.companyUser?.company?.company_name}`,
        body: formValue.message,
      })
      .subscribe(
        (response) => {
          console.log('Form submitted successfully', response);
          this._notificationService.showNotification(
            'Query sent successfully',
            NotificationType.INFO,
          );
        },
        (error) => {
          this._notificationService.showNotification(
            error?.error?.err_message || 'Error submitting query',
            NotificationType.ERROR,
          );
          console.error('Error submitting form', error);
        },
      );
  }

  submitChangePassword() {
    if (!this.passwordForm.valid) {
      this.passwordForm.markAllAsTouched();
      return;
    }
    const formValue = this.passwordForm.value;

    this.isChagingPassword = true;
    this._api
      .put(
        `/api/users/${this.localCompanyData?.companyUser?.user?._id}/password`,
        {
          newPassword: formValue.newPassword,
          oldPassword: formValue.currentPassword,
        },
      )
      .subscribe(
        (response) => {
          this.isChagingPassword = false;
          this.passwordForm.reset();
          this._notificationService.showNotification(
            'Password changed',
            NotificationType.INFO,
          );
        },
        (error) => {
          this.isChagingPassword = false;
          if (error.status === 403) {
            this._notificationService.showNotification(
              'Old Password eneterd is not correct.',
              NotificationType.ERROR,
            );
          } else {
            this._notificationService.showNotification(
              'We are not able to process your request.',
              NotificationType.ERROR,
            );
          }

          console.error('Error submitting form', error);
        },
      );
  }

  changePassword() {
    this.passwordForm.markAllAsTouched();
    if (this.passwordForm.valid) {
      this.submitChangePassword();
      return;
    }
  }

  loadCollaborators() {
    this._api
      .get(
        `/api/company/collaborators/${this.localCompanyData?.companyUser?.company?._id}`,
      )
      .subscribe((data: any) => {
        this.collaborators = data;
      });
  }

  toggleCollaboratorForm() {
    this.showCollaboratorForm = !this.showCollaboratorForm;
  }

  toggleMenu() {
    this.showMenu = !this.showMenu;
  }
  submitCollaborator() {
    if (!this.collaboratorForm.valid) {
      this.collaboratorForm.markAllAsTouched();
      return;
    }

    const formValue = this.collaboratorForm.value;
    this.loading = 'add';
    // Perform API call to add collaborator
    this._api
      .post('/api/company/addCollaborator?external=true', {
        ...formValue,
        user_type: this.editCollaborator ? this.editCollaborator.role : 'user',
        collaborator: 1,
        company_id: this.localCompanyData?.companyUser?.company?._id,
        invitee: this.localCompanyData?.companyUser?.user?.name,
      })
      .subscribe(
        (response) => {
          this.loadCollaborators();
          this._notificationService.showNotification(
            'Collaborator added successfully',
            NotificationType.INFO,
          );
          this.loading = null;
          this.showCollaboratorForm = false;
          this.editCollaborator = null;
          this.collaboratorForm.reset();
        },
        (err) => {
          this.loading = null;
          this._notificationService.showNotification(
            err?.error?.message || 'Error adding collaborator',
            NotificationType.ERROR,
          );
        },
      );
  }

  deleteCollaborator(collaboratorId: string) {
    const dialogRef = this._dialogModal.open(ConfirmComponent, {
      maxWidth: '350px',
      maxHeight: '300px',
      data: { name: 'User' },
    });
    dialogRef.componentInstance.deleteUser = () => this.delete(collaboratorId);
  }

  delete(collaboratorId: string) {
    const url = `/api/users/deleteCollaborator/${collaboratorId}`;
    this.loading = 'delete';
    this._api.delete(url).subscribe(
      (response) => {
        this._notificationService.showNotification(
          'Collaborator deleted',
          NotificationType.INFO,
        );
        this.loading = null;
        this._dialogModal.closeAll();
        this.loadCollaborators(); // Reload collaborators list after delete
      },
      (err) => {
        this.loading = null;
        this._notificationService.showNotification(
          err?.error?.message || 'Error deleting collaborator',
          NotificationType.ERROR,
        );
      },
    );
  }

  // Method to show the reset password form
  openResetPasswordForm(collaboratorId: Collaborator) {
    this.selectedCollaboratorId = collaboratorId;
  }

  openEditForm(collaboratorId: Collaborator) {
    this.editCollaborator = collaboratorId;

    this.collaboratorForm.patchValue({
      name: this.editCollaborator.name,
      email: this.editCollaborator.email,
    });
  }

  // Submit the form and call the reset password API
  onResetPassword() {
    if (this.resetPasswordForm.valid) {
      const { newPassword, confirmPassword } = this.resetPasswordForm.value;
      if (newPassword !== confirmPassword) {
        alert('Passwords do not match!');
        return;
      }

      const userId = this.selectedCollaboratorId?._id;

      // Call the reset password API with the userId in query params
      const resetPasswordUrl = `/api/users/${userId}/resetpassword?genie=true`;
      this.loading = 'reset';
      this._api.put(resetPasswordUrl, { newPassword }).subscribe(
        (response: any) => {
          this._notificationService.showNotification(
            'Password reset successfull',
            NotificationType.INFO,
          );
          this.loading = null;
          this.selectedCollaboratorId = null;
          this.resetPasswordForm.reset();
        },
        (err: any) => {
          this.loading = null;
          this._notificationService.showNotification(
            err?.error?.message || 'Error resetting password',
            NotificationType.ERROR,
          );
        },
      );
    }
  }

  closeResetPassword() {
    this.selectedCollaboratorId = null;
    this.editCollaborator = null;
  }
}

interface Company {
  genieSubscription: {
    startDate: Date | null;
    endDate: Date | null;
    gamesAvailable: number;
    usage: {
      gamesUsed: number;
    };
    _id: string;
  };
}
